import { EMAIL_FOOTER_PLACEHOLDER } from './sharedTemplates';

interface AnonymousPUITemplateProps {
  anonymousIdentifier: string;
  productName: string;
  intakeSections: FormattedIntakeSection[];
  partnerIntakeSections?: FormattedIntakeSection[];
  customMessages?: {
    greeting?: string;
    introParagraph?: string;
    productReferenceNote?: string;
    outroRequestIntro?: string;
    outroRequestItems?: string[];
    outroClosingInstruction?: string;
    closingGreeting?: string;
    closingSignature?: string;
  };
}

const DEFAULT_MESSAGES = {
  greeting: 'Dear Underwriting Team,',
  introParagraph: (hasPartner: boolean) =>
    `We are requesting a preliminary underwriting inquiry for the following anonymized client${hasPartner ? ' and their partner' : ''}. This information is being submitted to evaluate potential eligibility and coverage options before proceeding with a full application.`,
  productReferenceNote: 'Please reference this ID in all correspondence regarding this inquiry.',
  outroRequestIntro: 'Please review this information and provide preliminary feedback on:',
  outroRequestItems: [
    'Likely approval status',
    'Any health conditions that may impact underwriting',
    'Recommended next steps or additional information needed',
  ],
  outroClosingInstruction: (anonymousIdentifier: string) =>
    `Please reply with your preliminary assessment using the reference ID <strong>${anonymousIdentifier}</strong>.`,
  closingGreeting: 'Best regards,',
  closingSignature: 'Waterlily Team',
};

function renderSectionsAsTable(sections: FormattedIntakeSection[]): string {
  if (!sections || sections.length === 0) {
    return '<p>No additional client information available.</p>';
  }

  return sections
    .map(
      section => `
      <div style="margin-bottom: 20px; padding: 15px; background-color: #f9f9f9; border-radius: 5px;">
        <div style="font-weight: bold; font-size: 14px; margin-bottom: 10px; color: #0F0D30; border-bottom: 1px solid #ddd; padding-bottom: 5px;">
          ${section.category}
        </div>
        <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
          ${section.items
            .map(
              item => `
            <tr>
              ${item.label ? `<td style="padding: 4px 8px; vertical-align: top; font-weight: 600; color: #555; width: 30%;">${item.label}:</td>` : ''}
              <td style="padding: 4px 8px; vertical-align: top; color: #333;${item.label ? '' : ' font-weight: 600;'}">${item.value}</td>
            </tr>
          `,
            )
            .join('')}
        </table>
      </div>
    `,
    )
    .join('');
}

export function getAnonymousPUIEmailTemplate({
  anonymousIdentifier,
  productName,
  intakeSections,
  partnerIntakeSections,
  customMessages,
}: AnonymousPUITemplateProps) {
  const hasPartner = !!partnerIntakeSections;

  const messages = {
    greeting: customMessages?.greeting ?? DEFAULT_MESSAGES.greeting,
    introParagraph:
      customMessages?.introParagraph ?? DEFAULT_MESSAGES.introParagraph(hasPartner),
    productReferenceNote:
      customMessages?.productReferenceNote ?? DEFAULT_MESSAGES.productReferenceNote,
    outroRequestIntro:
      customMessages?.outroRequestIntro ?? DEFAULT_MESSAGES.outroRequestIntro,
    outroRequestItems:
      customMessages?.outroRequestItems ?? DEFAULT_MESSAGES.outroRequestItems,
    outroClosingInstruction:
      customMessages?.outroClosingInstruction ??
      DEFAULT_MESSAGES.outroClosingInstruction(anonymousIdentifier),
    closingGreeting: customMessages?.closingGreeting ?? DEFAULT_MESSAGES.closingGreeting,
    closingSignature: customMessages?.closingSignature ?? DEFAULT_MESSAGES.closingSignature,
  };

  const html = `<!DOCTYPE html>
<html style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; box-sizing: border-box; font-size: 14px; margin: 0;">

<head>
    <meta name="viewport" content="width=device-width" />
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
    <title>Anonymous Preliminary Underwriting Inquiry</title>

    <style type="text/css">
        img {
            max-width: 100%;
        }

        body {
            -webkit-font-smoothing: antialiased;
            -webkit-text-size-adjust: none;
            width: 100% !important;
            height: 100%;
            line-height: 1.6em;
        }

        body {
            background-color: #f6f6f6;
        }

        @media only screen and (max-width: 640px) {
            body {
                padding: 0 !important;
            }

            h1 {
                font-weight: 800 !important;
                margin: 20px 0 5px !important;
                font-size: 22px !important;
            }

            h2 {
                font-weight: 800 !important;
                margin: 20px 0 5px !important;
                font-size: 18px !important;
            }

            h3 {
                font-weight: 800 !important;
                margin: 20px 0 5px !important;
                font-size: 16px !important;
            }

            .container {
                padding: 0 !important;
                width: 100% !important;
            }

            .content {
                padding: 0 !important;
            }

            .content-wrap {
                padding: 10px !important;
            }
        }

        .section {
            margin: 20px 0;
            padding: 15px;
            background-color: #f9f9f9;
            border-radius: 5px;
        }

        .section-title {
            font-weight: bold;
            font-size: 16px;
            margin-bottom: 10px;
            color: #0F0D30;
        }

        .info-row {
            margin: 8px 0;
        }

        .label {
            font-weight: 600;
            color: #333;
        }
    </style>
</head>

<body
    style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; -webkit-font-smoothing: antialiased; -webkit-text-size-adjust: none; width: 100% !important; height: 100%; line-height: 1.6em; background-color: #f6f6f6; margin: 0;"
    bgcolor="#f6f6f6">

    <table class="body-wrap"
        style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; width: 100%; background-color: #f6f6f6; margin: 0;"
        bgcolor="#f6f6f6">
        <tr
            style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; margin: 0;">
            <td style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; vertical-align: top; margin: 0;"
                valign="top"></td>
            <td class="container" width="600"
                style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; vertical-align: top; display: block !important; max-width: 600px !important; clear: both !important; margin: 0 auto;"
                valign="top">
                <div class="content"
                    style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; max-width: 600px; display: block; margin: 0 auto; padding: 20px;">
                    <table class="main" width="100%" cellpadding="0" cellspacing="0"
                        style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; border-radius: 3px; background-color: #fff; margin: 0; border: 1px solid #e9e9e9;"
                        bgcolor="#fff">
                        <tr
                            style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; margin: 0;">
                            <td class="content-wrap"
                                style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; vertical-align: top; margin: 0; padding: 30px;"
                                valign="top">
                                <table width="100%" cellpadding="0" cellspacing="0"
                                    style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; margin: 0;">

                                    <tr style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; margin: 0;">
                                        <td class="content-block"
                                            style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; vertical-align: top; margin: 0; padding: 0 0 20px;"
                                            valign="top">
                                            <h2 style="margin: 0 0 10px; color: #0F0D30;">Anonymous Preliminary Underwriting Inquiry</h2>
                                        </td>
                                    </tr>

                                    ${
                                      messages.greeting || messages.introParagraph
                                        ? `
                                    <tr style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; margin: 0;">
                                        <td class="content-block"
                                            style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; vertical-align: top; margin: 0; padding: 0 0 20px;"
                                            valign="top">
                                            ${messages.greeting ? `<p style="margin: 0 0 10px;">${messages.greeting}</p>` : ''}
                                            ${messages.introParagraph ? `<p style="margin: 10px 0;">${messages.introParagraph}</p>` : ''}
                                        </td>
                                    </tr>
                                    `
                                        : ''
                                    }

                                    <tr style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; margin: 0;">
                                        <td class="content-block"
                                            style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; vertical-align: top; margin: 0; padding: 0 0 20px;"
                                            valign="top">
                                            <div class="section">
                                                <div class="section-title">Product & Reference Information</div>
                                                <div class="info-row">
                                                    <span class="label">Product:</span> ${productName}
                                                </div>
                                                <div class="info-row">
                                                    <span class="label">Anonymous ID:</span> ${anonymousIdentifier}
                                                </div>
                                                ${messages.productReferenceNote ? `<p style="margin: 10px 0 0; font-size: 12px; color: #666;">${messages.productReferenceNote}</p>` : ''}
                                            </div>
                                        </td>
                                    </tr>

                                    <tr style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; margin: 0;">
                                        <td class="content-block"
                                            style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; vertical-align: top; margin: 0; padding: 0 0 20px;"
                                            valign="top">
                                            ${partnerIntakeSections ? `<h3 style="margin: 0 0 15px; color: #0F0D30; font-size: 16px;">Primary Client</h3>` : ''}
                                            ${renderSectionsAsTable(intakeSections)}
                                        </td>
                                    </tr>

                                    ${
                                      partnerIntakeSections
                                        ? `
                                    <tr style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; margin: 0;">
                                        <td class="content-block"
                                            style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; vertical-align: top; margin: 0; padding: 0 0 20px;"
                                            valign="top">
                                            <h3 style="margin: 0 0 15px; color: #0F0D30; font-size: 16px;">Partner</h3>
                                            ${renderSectionsAsTable(partnerIntakeSections)}
                                        </td>
                                    </tr>
                                    `
                                        : ''
                                    }

                                    ${
                                      messages.outroRequestIntro ||
                                      (messages.outroRequestItems && messages.outroRequestItems.length > 0) ||
                                      messages.outroClosingInstruction
                                        ? `
                                    <tr style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; margin: 0;">
                                        <td class="content-block"
                                            style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; vertical-align: top; margin: 0; padding: 0 0 20px;"
                                            valign="top">
                                            ${messages.outroRequestIntro ? `<p style="margin: 10px 0;">${messages.outroRequestIntro}</p>` : ''}
                                            ${
                                              messages.outroRequestItems && messages.outroRequestItems.length > 0
                                                ? `
                                            <ul style="margin: 10px 0; padding-left: 20px;">
                                                ${messages.outroRequestItems.map(item => `<li>${item}</li>`).join('')}
                                            </ul>
                                            `
                                                : ''
                                            }
                                            ${messages.outroClosingInstruction ? `<p style="margin: 10px 0;">${messages.outroClosingInstruction}</p>` : ''}
                                        </td>
                                    </tr>
                                    `
                                        : ''
                                    }

                                    ${
                                      messages.closingGreeting || messages.closingSignature
                                        ? `
                                    <tr style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; margin: 0;">
                                        <td class="content-block"
                                            style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; vertical-align: top; margin: 0; padding: 0 0 20px;"
                                            valign="top">
                                            ${messages.closingGreeting ? `${messages.closingGreeting}<br />` : ''}
                                            ${messages.closingSignature || ''}
                                        </td>
                                    </tr>
                                    `
                                        : ''
                                    }
                                </table>
                            </td>
                        </tr>
                    </table>
                    ${EMAIL_FOOTER_PLACEHOLDER}
                </div>
            </td>
            <td style="font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; box-sizing: border-box; font-size: 14px; vertical-align: top; margin: 0;"
                valign="top"></td>
        </tr>
    </table>
</body>

</html>`;
  return html;
}
